import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, Image as ImageIcon, FileText } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
});

const emptyForm = {
  name: '',
  role: '',
  technique: '',
  photo_url: '',
  pdf_url: '',
  display_order: 0,
  is_active: true,
};

const LiveExhibitionAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/live-exhibition/admin/list`, { headers: authHeaders() });
      const json = await res.json();
      if (json.success) setItems(json.data || []);
    } catch (e) {
      setError('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditId(null);
    setError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '',
      role: item.role || '',
      technique: item.technique || '',
      photo_url: item.photo_url || '',
      pdf_url: item.pdf_url || '',
      display_order: item.display_order ?? 0,
      is_active: item.is_active === 1 || item.is_active === true,
    });
    setEditId(item.id);
    setError('');
    setShowForm(true);
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
        body: fd,
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Upload endpoint error');
      const data = await res.json();
      const url = data.fileUrl || data.url || data.s3Location || '';
      if (url) setForm((f) => ({ ...f, photo_url: url }));
      else throw new Error('No URL returned');
    } catch (err) {
      setError('อัพโหลดรูปภาพไม่สำเร็จ: ' + err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('กรุณากรอกชื่อช่างฝีมือ'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role.trim() || null,
        technique: form.technique.trim() || null,
        photo_url: form.photo_url.trim() || null,
        pdf_url: form.pdf_url.trim() || null,
        display_order: Number(form.display_order) || 0,
        is_active: form.is_active ? 1 : 0,
      };
      const url = editId
        ? `${API_BASE}/live-exhibition/admin/${editId}`
        : `${API_BASE}/live-exhibition/admin`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Save failed');
      await load();
      setShowForm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fetch(`${API_BASE}/live-exhibition/admin/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      await load();
    } catch (e) {
      setError('ลบไม่สำเร็จ');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Live Exhibition Management</h1>
          <p className="text-sm text-gray-500 mt-1">จัดการรายชื่อช่างฝีมือในนิทรรศการสด (พื้นที่สาธิต)</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          เพิ่มช่างฝีมือ
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">ทั้งหมด</p>
          <p className="text-2xl font-bold text-gray-800">{items.length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">แสดงอยู่</p>
          <p className="text-2xl font-bold text-green-600">{items.filter((i) => i.is_active).length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">ซ่อนอยู่</p>
          <p className="text-2xl font-bold text-gray-400">{items.filter((i) => !i.is_active).length}</p>
        </div>
      </div>

      {/* Error */}
      {error && !showForm && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">กำลังโหลด...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-4xl mb-3">🎨</div>
            <p className="text-gray-500">ยังไม่มีรายการ กด "เพิ่มช่างฝีมือ" เพื่อเริ่มต้น</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                {/* Photo thumbnail */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  {item.photo_url ? (
                    <img src={item.photo_url} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 truncate" style={{ fontFamily: 'Prompt' }}>
                      {item.name}
                    </span>
                    {item.technique && (
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                        {item.technique}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {item.is_active ? 'แสดง' : 'ซ่อน'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5 truncate" style={{ fontFamily: 'Prompt' }}>
                    {item.role || '—'}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    {item.pdf_url && (
                      <a href={item.pdf_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700">
                        <FileText className="w-3 h-3" /> PDF
                      </a>
                    )}
                    <span className="text-xs text-gray-400">ลำดับ: {item.display_order}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openEdit(item)}
                    className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteId(item.id)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Drawer */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-800">
                  {editId ? 'แก้ไขข้อมูลช่างฝีมือ' : 'เพิ่มช่างฝีมือใหม่'}
                </h2>
                <button onClick={() => setShowForm(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ชื่อช่างฝีมือ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="เช่น อรพรรณ หอมเทียน"
                    style={{ fontFamily: 'Prompt' }}
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">บทบาท / ตำแหน่ง</label>
                  <input
                    type="text"
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="เช่น ช่างสิบหมู่ งานประดับกระจก"
                    style={{ fontFamily: 'Prompt' }}
                  />
                </div>

                {/* Technique */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เทคนิค / สาขา</label>
                  <input
                    type="text"
                    value={form.technique}
                    onChange={(e) => setForm((f) => ({ ...f, technique: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="เช่น งานประดับกระจก"
                    style={{ fontFamily: 'Prompt' }}
                  />
                </div>

                {/* Photo URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL รูปภาพ</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.photo_url}
                      onChange={(e) => setForm((f) => ({ ...f, photo_url: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="https://..."
                    />
                    <label className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm font-medium text-gray-600 transition-colors whitespace-nowrap">
                      {uploadingPhoto ? (
                        <span className="flex items-center gap-1"><span className="animate-spin">⏳</span> กำลังอัพโหลด</span>
                      ) : (
                        <><ImageIcon className="w-4 h-4" /> อัพโหลด</>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={handleUploadPhoto} disabled={uploadingPhoto} />
                    </label>
                  </div>
                  {form.photo_url && (
                    <img src={form.photo_url} alt="preview" className="mt-2 h-24 w-24 object-cover rounded-lg border border-gray-200" />
                  )}
                </div>

                {/* PDF URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL เอกสาร PDF</label>
                  <input
                    type="text"
                    value={form.pdf_url}
                    onChange={(e) => setForm((f) => ({ ...f, pdf_url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="https://..."
                  />
                </div>

                {/* Display order + Active */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ลำดับการแสดง</label>
                    <input
                      type="number"
                      value={form.display_order}
                      onChange={(e) => setForm((f) => ({ ...f, display_order: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">สถานะ</label>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border font-medium text-sm transition-colors ${
                        form.is_active
                          ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                          : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {form.is_active ? <><Eye className="w-4 h-4" /> แสดง</> : <><EyeOff className="w-4 h-4" /> ซ่อน</>}
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 px-6 pb-6">
                <button onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                  ยกเลิก
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60">
                  {saving ? <><span className="animate-spin">⏳</span> กำลังบันทึก</> : <><Save className="w-4 h-4" /> บันทึก</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
            <AlertDialogDescription>
              คุณแน่ใจหรือไม่ที่จะลบรายการนี้? การดำเนินการนี้ไม่สามารถย้อนกลับได้
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LiveExhibitionAdminPage;
